import { ResizeNotifier } from '@theme/critical';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that automatically sizes text to fit its container width.
 */
class JumboText extends HTMLElement {
  connectedCallback() {
    // CRITICAL: Hide element IMMEDIATELY to prevent CLS
    // Element must be hidden before any layout calculations
    this.style.visibility = 'hidden';
    this.style.opacity = '0';

    // Initial calculation - use requestAnimationFrame to batch with other layout work
    requestAnimationFrame(this.#handleResize);

    // Defer intersection observer setup to reduce main-thread work
    if (this.dataset.textEffect && this.dataset.textEffect !== 'none' && !prefersReducedMotion()) {
      const requestIdleCallback = typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : function(cb) { setTimeout(cb, 100); };

      requestIdleCallback(() => {
        this.#setIntersectionObserver();
      }, { timeout: 1000 });
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    if (this.dataset.textEffect && this.dataset.textEffect !== 'none' && !prefersReducedMotion()) {
      this.intersectionObserver?.disconnect();
    }
  }

  /**
   * Sets the intersection observer to calculate the optimal font size when the text is in view
   */
  #setIntersectionObserver() {
    // The threshold could be different based on the repetition of the animation.
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('jumbo-text-visible');
            if (this.dataset.animationRepeat === 'false') {
              this.intersectionObserver.unobserve(entry.target);
            }
          } else {
            this.classList.remove('jumbo-text-visible');
          }
        });
      },
      { threshold: 0.3 }
    );

    this.intersectionObserver.observe(this);
  }

  /**
   * Calculates the optimal font size to make the text fit the container width
   * OPTIMIZED: Element is already hidden on connectedCallback, so no layout shift occurs
   */
  #calculateOptimalFontSize = () => {
    // Check for empty text
    if (!this.textContent?.trim()) {
      return;
    }

    // Batch all reads first to avoid forced reflows
    const containerWidth = this.offsetWidth;
    if (containerWidth <= 0) return;

    // Disconnect the resize observer
    this.#resizeObserver.disconnect();

    // Use requestAnimationFrame to batch style writes
    requestAnimationFrame(() => {
      // Element is already hidden from connectedCallback - no need to hide again
      // This prevents any visible layout shift

      // Start with a minimal font size
      this.style.fontSize = '1px';

      // Find the optimal font size (this will do its own batching)
      const fontSize = findOptimalFontSize(this, containerWidth);

      // Apply the final size
      this.style.fontSize = `${fontSize}px`;

      // Add ready class BEFORE showing (for any CSS transitions)
      this.classList.add('ready');

      // Use another frame to ensure font size is applied before revealing
      requestAnimationFrame(() => {
        // Reveal with smooth transition to prevent any visual pop
        this.style.visibility = 'visible';
        this.style.opacity = '1';
        this.style.transition = 'opacity 0.3s ease-in-out';
      });

      // Reconnect the resize observer
      this.#resizeObserver.observe(this);
    });
  };

  #handleResize = () => {
    // Batch all layout reads first
    const rect = this.getBoundingClientRect();
    const bottom = rect.bottom + window.scrollY;
    const documentHeight = document.documentElement.offsetHeight;
    const distanceFromBottom = documentHeight - bottom;
    const shouldCapText = distanceFromBottom <= 100;

    // Batch all writes together
    this.dataset.capText = shouldCapText.toString();
    
    // Calculate font size after batching the cap text update
    this.#calculateOptimalFontSize();
  };

  #resizeObserver = new ResizeNotifier(this.#handleResize);
}

/**
 * Checks if text with the given font size overflows the container
 * @param {HTMLElement} element - The element to check
 * @param {number} containerWidth - The width of the container
 * @param {number} size - Font size to check
 * @returns {boolean} - True if text overflows
 */
function checkTextOverflow(element, containerWidth, size) {
  // Write style first
  element.style.fontSize = `${size}px`;
  // Then read layout property (this is unavoidable but we batch all writes before this read)
  return element.scrollWidth > containerWidth;
}

/**
 * Find optimal font size using binary search
 * Optimized to minimize forced reflows by batching style writes
 * @param {HTMLElement} element - The text element
 * @param {number} containerWidth - Available width
 * @returns {number} - The optimal font size
 */
function findOptimalFontSize(element, containerWidth) {
  // Binary search parameters - HEAVILY optimized for minimal reflows
  let minSize = 1;
  let maxSize = 500;
  const precision = 2; // Increased to 2px for faster convergence (less precise but faster)

  // Smart initial guess based on container width and text length
  const textLength = element.textContent?.length || 0;
  let fontSize = Math.min(maxSize, Math.sqrt(containerWidth) * (15 / Math.sqrt(Math.max(1, textLength))));

  // Adjust initial bounds based on first check
  if (checkTextOverflow(element, containerWidth, fontSize)) {
    maxSize = fontSize;
  } else {
    minSize = fontSize;
  }

  // Binary search implementation
  // CRITICAL: Reduced to 8 iterations maximum (down from 15)
  // Element is now hidden during calculation, so slight imprecision is invisible
  // This cuts forced reflows by ~47% (from 15 to 8 iterations)
  let iterations = 0;
  const MAX_ITERATIONS = 8;

  while (maxSize - minSize > precision && iterations < MAX_ITERATIONS) {
    fontSize = (minSize + maxSize) / 2;

    // Check overflow (this does a write-then-read, but we minimize iterations)
    if (checkTextOverflow(element, containerWidth, fontSize)) {
      maxSize = fontSize;
    } else {
      minSize = fontSize;
    }

    iterations++;
  }

  // Add a small safety margin to prevent overflow
  return minSize * 0.98;
}

// Register once
if (!customElements.get('jumbo-text')) {
  customElements.define('jumbo-text', JumboText);
}
